/*

  Usage:

  const Page = () => {
    const buttonRef = useRef(null)
    const [ isVisible ] = useVisibility(buttonRef)

    if (isVisible) {
      sendAnalyticData()
    }

    return (
      <Fragment>
        <div>
          <Text />
          <Text />
        <div>
        <button ref={buttonRef}>Click me</button>
      </Fragment>
    )
  }

  A hook is used to understand whether a user sees an dom element.

  Example:
    We have a banner on the page and it’s located at the very bottom of the page,
    to see it the page needs to be scrolled.
    We want to send analytics when the user sees this banner.
 */

import React, { useCallback, useMemo, useEffect, useState } from 'react'
import throttle from 'common/helpers/actions/throttle'
import getElementInViewport from 'common/helpers/getters/getElementInViewport'
import useEventListener from './useEventListener'


type ReturnType = [
  boolean,
  () => void
]


// initialValue (true) is used for the element that already visible on the page
const useVisibility = (elementRef: React.RefObject<HTMLElement|null>, initialValue?: boolean, once?: boolean): ReturnType => {
  const [ isVisible, setIsVisible ] = useState(Boolean(initialValue))

  const handler = useMemo(() => throttle(() => {
    const isVisible = Boolean(elementRef.current) && getElementInViewport(elementRef.current)

    if (once) {
      // check to protect it from edge cases if it can fire twice
      if (isVisible !== Boolean(initialValue)) {
        setIsVisible(isVisible)
      }
    }
    else {
      setIsVisible(isVisible)
    }
  }, 100), [ elementRef ])

  const removeScrollListener = useEventListener('scroll', handler)
  const removeResizeListener = useEventListener('resize', handler)

  const removeListeners = useCallback(() => {
    removeScrollListener()
    removeResizeListener()
  }, [ removeScrollListener, removeResizeListener ])

  useEffect(() => {
    const isVisible = Boolean(elementRef.current) && getElementInViewport(elementRef.current)

    if (once) {
      // check to protect it from edge cases if it can fire twice
      if (isVisible !== Boolean(initialValue)) {
        setIsVisible(isVisible)
      }
    }
    else {
      setIsVisible(isVisible)
    }
  }, [ elementRef, removeListeners ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (once && isVisible !== Boolean(initialValue)) {
      removeListeners()
    }
  }, [ isVisible, removeListeners ]) // eslint-disable-line react-hooks/exhaustive-deps

  return [ isVisible, removeListeners ]
}


export default useVisibility
